import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    NgZone,
    Renderer2,
    ViewChild,
    signal,
    computed,
} from "@angular/core";
import { AriaLive } from "../../services/aria.live.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Util } from "../../util/s25-util";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import {
    OccurrenceTimeColors,
    occurrenceTimeBandForRange,
    readOccurrenceTimeColors,
    readSliderRailOutsideColor,
} from "../../util/occurrence.time.colors";

@Component({
    selector: "s25-ng-slider",
    template: `@if (init) {
        <div class="slider-wrapper" [class.has-down-labels]="ranges().length">
            <div #slider class="rail" [style.background]="linearGradient" (mouseup)="onMouseUp()">
                @for (range of ranges(); track range; let i = $index) {
                    <div>
                        @if (S25Util.isDefined(range.start) && !range?.hideStart) {
                            @if (readonly) {
                                <div
                                    class="thumb read-only"
                                    [style.left.%]="range.position"
                                    [class.down-label]="range.startLabelBelow"
                                    [class.marker-detached]="range.startLabelMarkerDetached"
                                >
                                    <p
                                        class="slider-label"
                                        [class.down-label]="range.startLabelBelow"
                                        [style.--slider-label-offset.px]="range.startLabelOffsetPx ?? 0"
                                        tabindex="0"
                                        (mousedown)="setActiveLabel($event)"
                                        (focusin)="
                                            announceReadonlyThumb(range.startLabel, range.startFormat ?? range.start)
                                        "
                                    >
                                        <span class="label-text">{{ range.startLabel }}</span>
                                        <span>{{ range.startFormat || range.start }}</span>
                                    </p>
                                    <div class="thumb-anchor" aria-hidden="true"></div>
                                </div>
                            } @else {
                                <div
                                    class="thumb"
                                    role="slider"
                                    [attr.aria-label]="
                                        thumbAccessibleLabel(range.startLabel, range.startFormat ?? range.start)
                                    "
                                    [attr.aria-valuenow]="range.start"
                                    [attr.aria-valuetext]="range.startFormat ?? range.start"
                                    [attr.aria-valuemin]="minValue()"
                                    [attr.aria-valuemax]="maxValue()"
                                    [style.left.%]="range.position"
                                    [class.down-label]="range.startLabelBelow"
                                    [class.marker-detached]="range.startLabelMarkerDetached"
                                    (mousedown)="onMouseDown($event, range, i, 'start')"
                                    (keydown)="setValues(range, 'start', null, i, $event)"
                                >
                                    <div
                                        class="slider-label"
                                        [class.down-label]="range.startLabelBelow"
                                        [style.--slider-label-offset.px]="range.startLabelOffsetPx ?? 0"
                                        tabindex="0"
                                        (mousedown)="setActiveLabel($event)"
                                    >
                                        <span class="label-text">{{ range.startLabel }}</span>
                                        <span>{{ range.startFormat || range.start }}</span>
                                    </div>
                                    <div class="thumb-anchor" tabindex="0"></div>
                                </div>
                            }
                        }
                        @if (S25Util.isDefined(range.end) && !range?.hideEnd) {
                            @if (readonly) {
                                <div
                                    class="thumb read-only"
                                    [style.left.%]="range.position2"
                                    [class.down-label]="range.endLabelBelow"
                                    [class.marker-detached]="range.endLabelMarkerDetached"
                                >
                                    <p
                                        class="slider-label"
                                        [class.down-label]="range.endLabelBelow"
                                        [style.--slider-label-offset.px]="range.endLabelOffsetPx ?? 0"
                                        tabindex="0"
                                        (mousedown)="setActiveLabel($event)"
                                        (focusin)="
                                            announceReadonlyThumb(range.endLabel, range.endFormat ?? range.end, 'End')
                                        "
                                    >
                                        <span class="label-text">{{ range.endLabel }}</span>
                                        <span>{{ range.endFormat || range.end }}</span>
                                    </p>
                                    <div class="thumb-anchor" aria-hidden="true"></div>
                                </div>
                            } @else {
                                <div
                                    class="thumb"
                                    role="slider"
                                    [attr.aria-label]="
                                        thumbAccessibleLabel(range.endLabel, range.endFormat ?? range.end, 'End')
                                    "
                                    [attr.aria-valuenow]="range.end"
                                    [attr.aria-valuetext]="range.endFormat ?? range.end"
                                    [attr.aria-valuemin]="minValue()"
                                    [attr.aria-valuemax]="maxValue()"
                                    [style.left.%]="range.position2"
                                    [class.down-label]="range.endLabelBelow"
                                    [class.marker-detached]="range.endLabelMarkerDetached"
                                    (mousedown)="onMouseDown($event, range, i, 'end')"
                                    (keydown)="setValues(range, 'end', null, i, $event)"
                                >
                                    <div
                                        class="slider-label"
                                        [class.down-label]="range.endLabelBelow"
                                        [style.--slider-label-offset.px]="range.endLabelOffsetPx ?? 0"
                                        tabindex="0"
                                        (mousedown)="setActiveLabel($event)"
                                    >
                                        <span class="label-text">{{ range.endLabel }}</span>
                                        <span>{{ range.endFormat || range.end }}</span>
                                    </div>
                                    <div class="thumb-anchor" tabindex="0"></div>
                                </div>
                            }
                        }
                    </div>
                }
            </div>
        </div>
    }`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25SliderComponent implements OnInit {
    /** Fallback label width — the 9em min-width in s25.slider.scss plus padding and border. */
    private static readonly LABEL_WIDTH_PX = 160;
    /** Used when the rail has not been measured, e.g. the slider is not laid out yet. */
    private static readonly FALLBACK_OVERLAP_PERCENT = 18;

    @Input() model: SliderModel;
    @Input() readonly: boolean = true;

    @Output() onChange: EventEmitter<SliderModel> = new EventEmitter<SliderModel>();

    @ViewChild("slider") slider: ElementRef;

    init: boolean;
    ranges = signal<Range[]>([]);
    isDragging: boolean;
    sliderRange = computed<number>(() => this.maxValue() - this.minValue());
    currentRange: Range | null;
    currentThumb: Thumb | null;
    currentRangeIndex: number;
    linearGradient: string;
    dateFormat: string;
    timeFormat: string;
    is24Hour: boolean;
    activeLabel: HTMLDivElement | null;
    iniModel: SliderModel;
    minValue = signal<number>(0);
    maxValue = signal<number>(0);
    step: number = 0.25;
    numDays: number = 0;

    private occurrenceTimeColorCache: OccurrenceTimeColors | null = null;
    private railOutsideColor: string | null = null;

    constructor(
        private cd: ChangeDetectorRef,
        private zone: NgZone,
        private renderer: Renderer2,
        private elementRef: ElementRef,
    ) {}

    @HostListener("mousedown", ["$event"])
    onHostListenerMouseDown(event: MouseEvent) {
        this.onMouseDown(event, this.currentRange, this.currentRangeIndex, this.currentThumb);
    }

    onMouseDown(
        event: MouseEvent,
        range: typeof this.currentRange,
        index: typeof this.currentRangeIndex,
        thumb: typeof this.currentThumb,
    ) {
        if (thumb !== undefined) {
            if (!this.readonly) {
                this.isDragging = true;
                this.currentRange = range;
                this.currentRangeIndex = index;
                this.currentThumb = thumb;
            }
            this.setActiveLabel(event);
        }

        !this.readonly && this.setInitialThumbPosition(event.clientX, this.currentRange, this.currentThumb);
    }

    @HostListener("mousemove", ["$event"])
    onMouseMove(event: MouseEvent) {
        if (this.isDragging && !this.readonly)
            this.handleMouseMove(event, this.currentRange, this.currentRangeIndex, this.currentThumb);
    }

    @HostListener("mouseup")
    onMouseUp() {
        if (!this.readonly) {
            this.isDragging = false;
            this.currentRange = null;
            this.currentThumb = null;

            //// convert ranges back to date
            this.iniModel.ranges.forEach((range: Range, index: number) => {
                // Update the start property of the range in initMode
                range.start = new Date(range.start).setHours(this.model.ranges[index].start as number);
                range.start = new Date(range.start).setMinutes(((this.model.ranges[index].start as number) % 1) * 60);
                range.start = new Date(range.start);

                range.end = new Date(range.end).setHours(this.model.ranges[index].end as number);
                range.end = new Date(range.end).setMinutes(((this.model.ranges[index].end as number) % 1) * 60);
                range.end = new Date(range.end);
            });

            this.onChange.emit(this.iniModel);
        }
    }

    async ngOnInit() {
        try {
            if (this.model.type === "time") {
                this.dateFormat = await UserprefService.getS25DateTimeformat();
                this.timeFormat = await UserprefService.getS25Timeformat();
                this.is24Hour = await UserprefService.getIs24HourTime();
            }
        } catch (e) {
            console.error(e);
        }

        this.iniModel = S25Util.deepCopy(this.model);

        this.numDays = S25Util.date.diffDays(
            this.model.ranges.at(-1)?.start || this.model.ranges.at(-1)?.end,
            this.model.ranges.at(-1)?.end || this.model.ranges.at(-1)?.start,
        );

        this.maxValue.set(
            Math.max(...this.model.ranges.map((range) => this.convertToDecimal(range.end as Date))) + this.model.step,
        );
        this.minValue.set(
            Math.min(...this.model.ranges.map((range) => this.convertToDecimal(range.start as Date))) - this.model.step,
        );

        this.ranges.set(
            this.model.ranges.map((range: Range, index) => {
                range.start = this.convertToDecimal(range.start as Date);
                if (range.end) range.end = this.convertToDecimal(range.end as Date);

                range.position = this.getPosition(range.start as number);
                if (range.end) {
                    range.position2 = this.getPosition(range.end as number);
                }
                range.startFormat = this.setFormat(this.iniModel.ranges[index].start as Date);

                range.endFormat = range.end && this.setFormat(this.iniModel.ranges[index].end as Date);

                return range;
            }),
        );

        this.getGradient();

        this.init = true;

        this.cd.detectChanges();

        // Needs the rendered rail to know how much of it a label covers.
        this.layoutThumbLabels();

        this.cd.detectChanges();
    }

    /**
     * Event Start/End (the first range) label above the rail, additional time below, so a short
     * setup or pre-event never covers the event's own times. Each row is then spread horizontally
     * where labels would otherwise sit on top of each other.
     */
    private layoutThumbLabels(): void {
        const ranges = this.ranges();
        const markers: ThumbMarker[] = [];

        ranges.forEach((range, rangeIndex) => {
            const isEventRange = rangeIndex === 0;
            if (S25Util.isDefined(range.start) && !range.hideStart && range.position != null) {
                markers.push({ range, isEventRange, side: "start", position: range.position });
            }
            if (S25Util.isDefined(range.end) && !range.hideEnd && range.position2 != null) {
                markers.push({ range, isEventRange, side: "end", position: range.position2 });
            }
        });

        markers.sort((left, right) => left.position - right.position);

        this.spreadLabelRow(
            markers.filter((marker) => marker.isEventRange),
            false,
        );
        this.spreadLabelRow(
            markers.filter((marker) => !marker.isEventRange),
            true,
        );

        this.ranges.set([...ranges]);
    }

    /** Offset labels in one row (above or below the rail) so neighbours stop overlapping. */
    private spreadLabelRow(markers: ThumbMarker[], labelBelow: boolean): void {
        const overlapThreshold = this.labelOverlapThresholdPercent();
        const railWidthPx = this.railWidthPx();
        const labelWidthPx = this.labelWidthPx();
        let cluster: ThumbMarker[] = [];

        const flushCluster = () => {
            if (cluster.length) this.placeCluster(cluster, labelBelow, railWidthPx, labelWidthPx);
            cluster = [];
        };

        for (const marker of markers) {
            const previousMarker = cluster.at(-1);
            if (previousMarker && marker.position - previousMarker.position > overlapThreshold) {
                flushCluster();
            }
            cluster.push(marker);
        }
        flushCluster();
    }

    /**
     * Sit the cluster over the middle of the markers it covers and space its labels one label
     * apart, so a pair that only just overlaps only just moves and stays over its own marker.
     */
    private placeCluster(cluster: ThumbMarker[], labelBelow: boolean, railWidthPx: number, labelWidthPx: number): void {
        const markerPx = (marker: ThumbMarker) => (marker.position / 100) * railWidthPx;
        const centerPx = cluster.reduce((total, marker) => total + markerPx(marker), 0) / cluster.length;
        const firstPx = centerPx - ((cluster.length - 1) * labelWidthPx) / 2;

        cluster.forEach((marker, index) => {
            this.placeLabel(marker, labelBelow, firstPx + index * labelWidthPx - markerPx(marker), labelWidthPx);
        });
    }

    private placeLabel(marker: ThumbMarker, labelBelow: boolean, offsetPx: number, labelWidthPx: number): void {
        // Shifted this far the label no longer reads as sitting on its marker, so the caret is left
        // standing on its own and needs its own outline rather than the box's.
        const markerDetached = Math.abs(offsetPx) > labelWidthPx / 4;
        if (marker.side === "start") {
            marker.range.startLabelBelow = labelBelow;
            marker.range.startLabelOffsetPx = offsetPx;
            marker.range.startLabelMarkerDetached = markerDetached;
        } else {
            marker.range.endLabelBelow = labelBelow;
            marker.range.endLabelOffsetPx = offsetPx;
            marker.range.endLabelMarkerDetached = markerDetached;
        }
    }

    private railWidthPx(): number {
        return this.slider?.nativeElement?.getBoundingClientRect().width ?? 0;
    }

    /** Widest rendered label, so one spacing step clears every label in the row. */
    private labelWidthPx(): number {
        const labels: HTMLElement[] = Array.from(this.slider?.nativeElement?.querySelectorAll(".slider-label") ?? []);
        const widestPx = labels.reduce((widest, label) => Math.max(widest, label.getBoundingClientRect().width), 0);
        return widestPx || S25SliderComponent.LABEL_WIDTH_PX;
    }

    /** How much of the rail one label covers — markers closer than this would overlap. */
    private labelOverlapThresholdPercent(): number {
        const railWidthPx = this.railWidthPx();
        if (!railWidthPx) return S25SliderComponent.FALLBACK_OVERLAP_PERCENT;
        return Math.min(40, (this.labelWidthPx() / railWidthPx) * 100);
    }

    handleMouseMove(event: MouseEvent, range: Range, index: number, thumb: Thumb) {
        if (!range) return;

        this.zone.run(() => {
            const rect = this.slider.nativeElement.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const percent = x / rect.width;

            const newValue = this.minValue() + percent * this.sliderRange();

            const roundedValue = Math.round(newValue / this.step) * this.step;

            const value = Math.max(this.minValue(), Math.min(this.maxValue(), roundedValue));

            this.setValues(range, thumb, value, index);
        });
    }

    setValues(range: Range, thumb: Thumb, value: number, index: number, event?: KeyboardEvent) {
        const step = this.step;
        const allRanges = this.model.ranges;
        const positionProp = thumb === "start" ? "position" : "position2";
        const formatProp = thumb === "start" ? "startFormat" : "endFormat";

        if (event) {
            event.key !== "Tab" && event.preventDefault();

            this.setActiveLabel(event);

            const keyMod =
                event.key === "ArrowRight" || event.key === "ArrowUp"
                    ? step
                    : event.key === "ArrowLeft" || event.key === "ArrowDown"
                      ? -step
                      : 0;

            value = (range[thumb] as number) + keyMod;
        }

        const position = this.getPosition(value);
        let maxValue: number, minValue: number, maxPosition: number, minPosition: number;

        if (thumb === "start") {
            minValue = (allRanges[index + 1]?.start ?? this.minValue() - step) as number;
            minPosition = allRanges[index + 1]?.position;

            if (index === 0) {
                maxValue = (range.end ?? allRanges[index - 1]?.start ?? this.maxValue() + step) as number;
                maxPosition = range.position2;
            } else {
                maxValue = allRanges[index - 1].start as number;
                maxPosition = allRanges[index - 1].position;
            }
        } else if (thumb === "end") {
            maxValue = (allRanges[index + 1]?.end ?? this.maxValue() + step) as number;
            minValue = (index === 0 ? range.start : allRanges[index - 1].end) as number;
            maxPosition = allRanges[index + 1]?.position2;
            minPosition = index === 0 ? range.position : allRanges[index - 1].position2;
        }

        range[thumb] = value >= maxValue ? maxValue - step : value <= minValue ? minValue + step : value;

        range[positionProp] =
            position >= maxPosition
                ? this.getPosition(maxValue, -step)
                : position <= minPosition
                  ? this.getPosition(minValue, step)
                  : position;

        // range[formatProp] = this.setFormat(range[thumb] as number);

        this.getGradient();

        this.cd.detectChanges();
    }

    setActiveLabel(event: Event) {
        if (this.activeLabel) {
            this.renderer.removeStyle(this.activeLabel, "z-index");
        }

        const target = event.target as HTMLElement | null;
        const thumb = target?.closest?.(".thumb") as HTMLDivElement | null;
        if (!thumb) {
            this.activeLabel = null;
            return;
        }

        this.activeLabel = thumb;
        this.renderer.setStyle(thumb, "z-index", 10);
    }

    getPosition(value: number, step: number = 0) {
        return ((value + step - this.minValue()) / this.sliderRange()) * 100;
    }

    setInitialThumbPosition(mouseX: number, range: Range, thumb: Thumb) {
        const rect = this.slider.nativeElement.getBoundingClientRect();
        const x = mouseX - rect.left;
        const percent = x / rect.width;

        const position = percent * 100;

        if (thumb === "start") {
            range.position = position;
        } else if (thumb === "end") {
            range.position2 = position;
        }
    }

    private colorTokenRoot(): Element {
        return this.elementRef.nativeElement ?? document.body;
    }

    private refreshOccurrenceTimeColors(): void {
        const root = this.colorTokenRoot();
        this.occurrenceTimeColorCache = readOccurrenceTimeColors(root);
        this.railOutsideColor = readSliderRailOutsideColor(root);
    }

    private ensureOccurrenceTimeColors(): OccurrenceTimeColors {
        if (!this.occurrenceTimeColorCache) {
            this.refreshOccurrenceTimeColors();
        }
        return this.occurrenceTimeColorCache!;
    }

    /** Occurrence Details: pre/post and setup/takedown keep fixed colors regardless of range index. */
    rangeRailColor(range: Range, rangeIndex: number): string {
        const band = occurrenceTimeBandForRange(range.startLabel, range.endLabel, rangeIndex);
        return this.ensureOccurrenceTimeColors()[band];
    }

    getGradient() {
        this.refreshOccurrenceTimeColors();
        const colors = this.occurrenceTimeColorCache!;
        const outsideColor = this.railOutsideColor!;

        if (!this.model.ranges[0].end) {
            this.linearGradient = `linear-gradient(to right, ${colors.event} ${this.model.ranges[0].position}%, ${outsideColor} ${this.model.ranges[0].position}%)`;
        } else {
            const rangeStart = [`linear-gradient(to right, ${outsideColor} 0%,`];
            const rangeEnd = [`${outsideColor} 100%)`];
            const lastIndex = this.model.ranges.length - 1;

            for (let i = lastIndex; i >= 0; i--) {
                const range = this.model.ranges[i];
                const prevRange = this.model.ranges[i - 1];
                const startPosition = (prevRange && range.hideStart ? prevRange : range)?.position;
                const endPosition = (prevRange && range.hideEnd ? prevRange : range)?.position2;
                const rangeColor = this.rangeRailColor(range, i);
                const outerBandColor =
                    i === lastIndex ? outsideColor : this.rangeRailColor(this.model.ranges[i + 1], i + 1);

                rangeStart.push(` ${outerBandColor} ${startPosition}%, ${rangeColor} ${startPosition}%,`);

                rangeEnd.unshift(` ${rangeColor} ${endPosition}%, ${outerBandColor} ${endPosition}%, `);
            }

            this.linearGradient = [...rangeStart, ...rangeEnd].join("");
        }
    }

    setFormat(date: Date) {
        if (this.model.type === "time") {
            return this.numDays > 0
                ? S25Datefilter.transform(date, this.dateFormat)
                : S25Datefilter.transform(date, this.timeFormat);

            // const baseDate = this.iniModel.ranges.at(-1).start;
            // console.log(S25Util.date.diffDays(baseDate, date), "diffDays");
            // const diffHours = S25Util.date.diffHours(baseDate, date);
            // console.log(diffHours, "diffHours");
            // const modifier = diffHours / 24;
            // console.log(modifier, "modifier");
            // const subtract = modifier * diffHours;
            // console.log(subtract, "subtract");
            // const timeString = time.toString().split(".");
            // const numHour = parseInt(timeString[0]);
            // const hour = !this.is24Hour && numHour > 12 ? numHour - 12 : timeString[0];
            //
            // let minutes = Math.round(+((+timeString[1] || 0) * 60).toString().slice(0, 4) / 100).toString();
            //
            // let suffix = "";
            // if (!this.is24Hour) {
            //     if (numHour === 0 || numHour === 24 || numHour < 12) {
            //         suffix = "AM";
            //     } else if (numHour >= 12) {
            //         suffix = "PM";
            //     }
            // }
            //
            // return `${!this.is24Hour && hour == 0 ? 12 : hour}:${minutes.length < 2 ? minutes.padEnd(2, "0") : minutes}${suffix}`;
        }
    }

    convertToDecimal(value: Date) {
        if (this.model.type === "time") {
            const diffDays = this.getDiffDays(value);
            const splitNum: Array<string | number> = S25Util.date.toTimeStr(value, true).split(":");
            splitNum[1] = parseInt(splitNum[1] as string) / 60;
            return parseInt(splitNum[0] as string) + diffDays + splitNum[1];
        }
    }

    getDiffDays(date: Date) {
        const baseDate = this.iniModel.ranges.at(-1).start;
        return Math.floor(S25Util.date.diffDays(baseDate, date) * 24);
    }

    protected thumbAccessibleLabel(
        label: string | null | undefined,
        formattedValue: string | number | null | undefined,
        fallbackLabel = "End",
    ): string {
        const name = label ?? fallbackLabel;
        if (formattedValue == null || formattedValue === "") {
            return name;
        }
        return `${name}, ${formattedValue}`;
    }

    protected announceReadonlyThumb(
        label: string | null | undefined,
        formattedValue: string | number | null | undefined,
        fallbackLabel = "End",
    ): void {
        void AriaLive.announce(this.thumbAccessibleLabel(label, formattedValue, fallbackLabel));
    }

    protected readonly S25Util = S25Util;
}

export type SliderModel = {
    type: "time" | "date" | "standard";
    // minValue: number;
    // maxValue: number;
    step: number;
    ranges: Range[]; // for multiple ranges, range precedent is determined by index - the first index is the central range, and successive indices (ie setup times, etc) encompass previous ranges
};

export type Range = {
    start?: number | Date;
    end?: number | Date | null;
    startLabel?: string;
    endLabel?: string | null;
    position?: number;
    position2?: number;
    startFormat?: string;
    endFormat?: string;
    hideStart?: boolean;
    hideEnd?: boolean;
    startLabelBelow?: boolean;
    endLabelBelow?: boolean;
    startLabelOffsetPx?: number;
    endLabelOffsetPx?: number;
    startLabelMarkerDetached?: boolean;
    endLabelMarkerDetached?: boolean;
};

export type Thumb = "start" | "end";

type ThumbMarker = {
    range: Range;
    isEventRange: boolean;
    side: Thumb;
    position: number;
};
